import {
  Component,
  EventEmitter,
  OnInit,
  Output,
  Input,
} from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';

import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Severity } from 'src/app/global/enums/severity';
import { AddEditModalComponent } from 'src/app/predictor-enterprise-license/shared/components/add-edit-modal/add-edit-modal.component';
import { AddUsersModalComponent } from 'src/app/predictor-enterprise-license/shared/components/add-users-modal/add-users-modal.component';
import { LicenseTierDropdown } from 'src/app/predictor-enterprise-license/shared/interface/license-tiers';
import {
  LicenseUser,
} from 'src/app/predictor-enterprise-license/shared/interface/license-user';
import {
  TableConfig,
} from 'src/app/predictor-enterprise-license/shared/interface/table-column';
import { EnterpriseLicenseService } from 'src/app/predictor/services/enterprise-license.service';
import { EnterpriseLicenseActions } from 'src/app/state/enterprise-license/enterprise-license.actions';
import { EnterpriseLicenseState } from 'src/app/state/enterprise-license/enterprise-license.state';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { SharedService } from 'src/app/predictor-enterprise-license/shared/services/shared.service';
import {
  LicenseTierViewModel,
  LicenseUserViewModel,
} from 'src/app/predictor-enterprise-license/shared/interface/Manager-Details';
import { LoggedInUserLicenseDetails } from 'src/app/predictor-enterprise-license/shared/interface/Enterprise-License';
import { Observable } from 'rxjs';

import { LicenseStudyAreaServiceService } from 'src/app/predictor/services/license-study-area-service.service';
import { AppDatePipe } from '../../pipes/app-date-pipe';


@Component({
  selector: 'app-license-details-view',
  templateUrl: './license-details-view.component.html',
  styleUrl: './license-details-view.component.scss',
  standalone: false,
})
export class LicenseDetailsViewComponent implements OnInit {
  @Input() licenseUsersDetails!: LoggedInUserLicenseDetails;
  public isPageVisible: boolean = true;
  public isEnterpriseDataAvailable: boolean = false;
  @Input()
  licenseUser!: Observable<any>;
  licenseNumber!: string;
  licenseDetail: any;
  searchString: string = '';
  detailsConfig: any; // Configuration for the detail component

  isLoading: boolean = false;
  totalRecords: number = 0;
  remainingUsers: any = 0;

  studyAreasList: any[] = [];
  totalArea: number = 0;
  remainingArea: number = 0;
  totalStudyAreas: number = 0;
  remainingAreaDisplay: string = '0';
  totalStudyAreasDisplay: string = '0';
  studySearchString: string = '';
  totalStudyAreaRecords: number = 0;

  licenseUsers: LicenseUserViewModel[] = [];
  rowsPerPage = 10;
  licenseTiers: LicenseTierDropdown[] = [];
  licenseTier: LicenseTierViewModel | null = null;
  sharedData: any;
  isManagerAdded = false;
  isMaxUsersReached = false;
  currentUserCount: number = 0;
  isLicenseTier3: boolean = false;

  constructor(
    private readonly enterpriseLicenseService: EnterpriseLicenseService,
    private readonly store: Store,
    private readonly router: Router,
    private readonly dialogService: DialogService,
    private readonly shareService: SharedService,
    private readonly studyAreaService: LicenseStudyAreaServiceService,
    private readonly appDatePipe: AppDatePipe
  ) { }

  ngOnInit(): void {
    this.licenseNumber = this.licenseUsersDetails?.licenseNumber;
    if (this.licenseNumber != undefined) {
      this.getLicenseDetails();
    }
    this.store
      .dispatch(new EnterpriseLicenseActions.GetLicenseTiers())
      .subscribe(() => {
        this.licenseTiers = this.store.selectSnapshot(
          EnterpriseLicenseState.licenseTiers
        );
      });
  }

  convertStringToTime(dateStr: string, daysToAdd: number): string {
    // Parse DD-MM-YYYY HH:mm
    let [datePart, timePart] = dateStr.split(' ');
    let [day, month, year] = datePart.split('-').map(Number);
    let [hours, minutes] = timePart?.split(':').map(Number) || [0, 0];

    // Build date
    let date = new Date(year, month - 1, day, hours, minutes);

    // Add days
    date.setDate(date.getDate() + daysToAdd);

    // Format as MM-DD-YYYY
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yyyy = date.getFullYear();

    return `${mm}-${dd}-${yyyy}`;
  }

  removeTimeDate(date: string) {
    let [datePart] = date.split(' ');
    let [day, month, year] = datePart.split('-').map(Number);

    // Ensure month and day are 2 digits
    let formattedMonth = String(month).padStart(2, '0');
    let formattedDay = String(day).padStart(2, '0');

    return `${formattedMonth}-${formattedDay}-${year}`;
  }

  getLicenseDetails(): void {
    this.enterpriseLicenseService
      .getLicenseDetails(this.licenseNumber)
      .subscribe({
        next: (data: any) => {
          // Assign license detail
          this.isPageVisible = false;
          this.isEnterpriseDataAvailable = true;
          this.licenseDetail = {
            licenseNumber: data.licenseNumber,
            licenseName: data.licenseName,
            clientName: data.clientName,
            licenseTier: data.licenseTier?.name,
            totalSeats: data.licenseTier?.maxUsers,
            remainingSeats: data.licenseTier?.remainingUsers,
            totalStudyArea: data.licenseTier?.maxArea,
            totalAreaUsed: data.licenseTier?.remainingArea,
            remainingAreaAvailable: data.licenseTier?.remainingArea,
            licenseStartDate: data.licenseStartDate,
            licenseEndDate: data.licenseEndDate,
            applicationBaseUrl: data.applicationBaseUrl,
          };

          // Map license tier (optional)
          this.licenseTier = data.licenseTier ?? null;

          // Map license users to table-ready data

          this.licenseUsers = (data.licenseUsers?.$values ?? []).map(
            (user: any) => ({
              createdDate: this.formatUserDate(user.createdDate),
              fullName: user.fullName,
              email: user.email,
              isActive: user.isActive === true ? 'Active' : 'Inactive',
            })
          );

          this.currentUserCount = this.licenseUsers.length;
          this.isLicenseTier3 = this.licenseDetail.licenseTier === 'Tier 3';

          if(this.isLicenseTier3) {
            this.licenseDetail.remainingSeats = '-';
            this.licenseDetail.totalSeats = '-';
          }

          this.detailsConfig = this.enterpriseLicenseDetails(
            this.licenseDetail
          );

          // Setup user table configuration
          this.userTableConfig = {
            ...this.userTableConfig,
            rows: this.licenseUsers,
          };

          // Calculate total users
          this.totalRecords = this.licenseUsers.length;

          // Remaining user slots
          this.remainingUsers = this.licenseDetail.remainingSeats;

          this.isMaxUsersReached = this.remainingUsers === 0;

          
          this.getStudyAreasList(this.licenseDetail?.licenseNumber);
        },
        error: (err: Severity) => {
          this.store.dispatch(
            new NotificationsActions.Toast('Error fetching data', err)
          );
        },
      });
  }

  convertSquareUnitToSqKm(areaMeasurement: number, unit: string) {
    let lowerUnit = unit.toLowerCase();
    if (lowerUnit === 'acres') {
      // 1 acre = 0.00404686 sq km
      return Math.round(areaMeasurement * 0.00404686 * 100) / 100;
    } else if (lowerUnit === 'hectares') {
      // 1 hectare = 0.01 sq km
      return Math.round(areaMeasurement * 0.01 * 100) / 100;
    }
    else if (lowerUnit === 'square-kilometers') {
      return areaMeasurement;
    }
    else {
      throw new Error('Invalid unit. Please use "acres" or "hectares".');
    }
  }

  getStudyAreasList(number: string): void {
    this.studyAreaService.getById(number).subscribe((data: any) => {
      this.studyAreasList = (data.$values ?? []).map((area: any) => ({
        creationTimestamp: this.formatUserDate(area.creationTimestamp),
        studyAreaName: area.studyAreaName,
        areaMeasurement: this.convertSquareUnitToSqKm(
          area.areaMeasurement,
          area.areaUnit
        ),
        createdBy: `${area.firstName} ${area.lastName}`,
      }));
      this.totalStudyAreas = this.licenseDetail?.totalStudyArea ?? 0;
      this.remainingArea = this.licenseDetail?.remainingAreaAvailable ?? 0;
      this.totalStudyAreasDisplay = new Intl.NumberFormat().format(this.licenseDetail.totalStudyArea);
      this.remainingAreaDisplay = new Intl.NumberFormat().format(this.licenseDetail.remainingAreaAvailable);      
      this.studyAreaTableConfig.rows = this.studyAreasList;
      this.totalArea = this.studyAreasList.length;
    });
  }

  userTableColumns: any[] = [
    { field: 'createdDate', header: 'Created Date' },
    { field: 'fullName', header: 'User Name' },
    { field: 'email', header: 'Email Address' },
    { field: 'isActive', header: 'Status' },
  ];

  studyAreaTableColumns: any[] = [
    { field: 'creationTimestamp', header: 'Date' },
    { field: 'studyAreaName', header: 'Study Area Name' },
    { field: 'areaMeasurement', header: 'Total Area(sq km)' },
    { field: 'createdBy', header: 'Created By' },
  ];

  enterpriseLicenseDetails(licenseDetail: any) {
    return {
      title: licenseDetail?.licenseName ?? 'N/A',
      id: licenseDetail?.licenseNumber ?? 'N/A',
      active: licenseDetail?.active ?? false,

      detailsItems: [
        {
          name: licenseDetail?.clientName ?? 'N/A',
          iconName: 'icon-person',
        },
        {
          name: `Activated on ${this.formatLicenseDate(
            licenseDetail?.licenseStartDate
          )}`,
          iconName: 'icon-today',
        },
        {
          name: `Expires on ${this.formatLicenseDate(
            licenseDetail?.licenseEndDate
          )}`,
          iconName: 'icon-event',
        },
        {
          name: licenseDetail?.licenseTier ?? 'N/A',
          iconName: 'icon-low-priority',
        },

        {
          name: `${this.currentUserCount} of ${licenseDetail?.totalSeats} users assigned`,
          iconName: 'icon-group',
        },
        {
          name: this.checkTierThree(licenseDetail?.totalStudyArea),
          iconName: 'icon-terrain',
        },
        {
          name: this.checkTierThree(licenseDetail?.totalAreaUsed),
          iconName: 'icon-landscape',
        },
      ],
      isEditVisible: false,
      isDeleteVisible: false,
      isLicenseStatusVisible: false,
    };
  }

  rows: number = 10;
  paginatorDropdownOptions: number[] = [5, 10, 20, 30, 50];

  onSort(event: any) {
    this.sortFunction.emit(event);
  }

  checkTierThree(AreaParameter: any): string {
  if (this.isLicenseTier3) {
    return '-';
  }

  if (AreaParameter== null) {
    return 'N/A';
  }

  return new Intl.NumberFormat().format(AreaParameter);
}

  formatLicenseDate(dateStr: string | null): string {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  }

  getMaxUsersForSpecificTier(
    licenseTiers: LicenseTierDropdown[],
    tier: string
  ): number | null {
    // Filter tiers matching the tierId
    const matchedTiers = licenseTiers.filter(
      (tierObj) => tierObj.name === tier
    );
    if (matchedTiers.length === 0) {
      return null; // or 0 or throw, depending on your preference
    }

    // Find the max of maxUsers from matched tiers
    return Math.max(...matchedTiers.map((t) => t.maxUsers));
  }


  // Status Dropdown Options
  statusOptions = [
    { label: 'Active', value: 'active', icon: 'icon-active' },
    { label: 'Inactive', value: 'inactive', icon: 'icon-inactive' },
    { label: 'Pending', value: 'pending', icon: 'icon-pending' },
  ];

  // Called on status dropdown change
  onStatusChange(newValue: boolean, user: LicenseUser) {
    user.active = newValue;
  }

  handleLicenseDetailAction(action: string, row: any) {
    if (action === 'edit') {
      this.openAddEditModal('edit', this.licenseDetail, this.licenseTiers);
    } else if (action === 'toggle') {
      this.toggleLicenseActivation();
    } else if (action === 'Delete') {
      // TODO: Implement delete functionality here
    }
  }

  @Output() sortFunction = new EventEmitter<any>();

  // Handle action from grid
  handleAction(action: string, row: LicenseUser) {
    if (action === 'Modify') {
      alert('Modify clicked for ' + row.licenseNumber);
    } else if (action === 'Delete') {
      console.log('Delete clicked', row);
    }
  }

  userTableConfig = {
    columns: this.userTableColumns,
    rows: this.licenseUsers || [],
  };

  studyAreaTableConfig: TableConfig = {
    columns: this.studyAreaTableColumns,
    rows: this.studyAreasList ?? [],
  };

  openAddManager(mode: 'user' | 'manager'): void {
    const headerVal = 'Add New Users';
    const ref: DynamicDialogRef = this.dialogService.open(
      AddUsersModalComponent,
      {
        width: '620px',
        dismissableMask: false,
        closable: true,
        styleClass: 'bottom-rounded basin-font-primary ',
        header: headerVal,
        data: {
          licenseNumber: this.licenseNumber,
          licenseUsers: this.licenseUsers,
          tierMaxUsers: this.licenseDetail.totalSeats,
          applicationBaseUrl: this.licenseDetail?.applicationBaseUrl ?? '',
          mode: mode,
        },
      }
    );
    ref.onClose.subscribe((added: boolean) => {
      if (added) {
        this.getLicenseDetails();
      }
    });
  }

  openAddEditModal(
    modalMode: 'add' | 'edit',
    licenseData?: any,
    licenseTiers?: LicenseTierDropdown[]
  ): void {
    const ref: DynamicDialogRef = this.dialogService.open(
      AddEditModalComponent,
      {
        width: '720px',
        dismissableMask: false,
        closable: true,
        styleClass: 'bottom-rounded basin-font-primary ',
        header:
          modalMode === 'add'
            ? 'Add New Enterprise License'
            : 'Edit Enterprise License',
        data: {
          mode: modalMode,
          licenseData,
          licenseTiers,
        },
      }
    );

    ref.onClose.subscribe((updatedLicense: any | null) => {
      this.licenseDetail = updatedLicense.license;
      this.detailsConfig = this.enterpriseLicenseDetails(this.licenseDetail);
    });
  }
  goBackToManageLicense(index: number): void {
    this.router.navigate(['manage-enterprises-license']);
    this.shareService.setActiveTabIndex(index);
  }

  toggleLicenseActivation(): void {
    if (
      this.licenseDetail?.licenseNumber == null ||
      this.licenseDetail?.licenseNumber == 'N/A'
    ) {
      this.store.dispatch(
        new NotificationsActions.Toast(
          'No license detail available to toggle activation.',
          Severity.Error
        )
      );
    }
    this.licenseDetail.active = !this.licenseDetail.active;

    // Persist changes - call backend update API
    this.enterpriseLicenseService
      .update(this.licenseNumber, this.licenseDetail)
      .subscribe({
        next: (updatedLicense) => {
          this.licenseDetail = updatedLicense;
          this.detailsConfig = this.enterpriseLicenseDetails(
            this.licenseDetail
          );
          this.store.dispatch(
            new NotificationsActions.Toast(
              'License updated successfully',
              Severity.Success
            )
          );
        },
        error: (err) => {
          console.error('Error updating license:', err);
          this.store.dispatch(
            new NotificationsActions.Toast(
              'Failed to update license',
              Severity.Error
            )
          );
        },
      });
  }

  @Output() searchStringChange = new EventEmitter<string>();

  onInputChange(value: string) {
    this.searchStringChange.emit(value);
  }

  onSearchChange(value: string): void {
    this.searchString = value;

    const filtered = this.licenseUsers.filter(
      (user) =>
        user.fullName.toLowerCase().includes(value.toLowerCase()) ||
        user.email.toLowerCase().includes(value.toLowerCase())
    );

    this.userTableConfig = {
      ...this.userTableConfig,
      rows: filtered,
    };

    this.totalRecords = filtered.length;
  }

  onStudyAreaChange(value: string): void {
    this.studySearchString = value;

    const filtered = this.studyAreasList.filter((area) => {
      const studyAreaName: string = area?.studyAreaName ?? '';
      const createdBy: string = area?.createdBy ?? '';

      return (
        studyAreaName.toLowerCase().includes(value.toLowerCase()) ||
        createdBy.toLowerCase().includes(value.toLowerCase())
      );
    });

    this.studyAreaTableConfig = {
      ...this.studyAreaTableConfig,
      rows: filtered,
    };

    this.totalStudyAreaRecords = filtered.length;
  }

  formatUserDate(dateStr: string | null): string {
    if (!dateStr) return 'N/A';

    return this.appDatePipe.transform(dateStr);
  }
}
